import { useEffect, useMemo } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
// The input types are inferred from these functions by withLowercasedUsername.
import { signIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useQueryClient } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import { router } from './router';
import { useRequestAdminAccess } from './hooks/useAdminAccess';
import { isAdministrator } from './lib/auth';
import { updateLastActivity } from './hooks/useSessionTimeout';
import { runCookieConsent } from './lib/cookie-consent';
import '@aws-amplify/ui-react/styles.css';

/**
 * Lowercases the username before handing the call to Amplify, because Cognito
 * usernames are case-sensitive. Port of the `services` object in
 * app.component.ts.
 *
 * The two-parameter signature is not a mistake. Amplify types these handlers
 * with two call signatures, one that takes its internal state-machine context
 * first and one that doesn't, and a function has to satisfy both to be
 * assignable. Only the one-argument form is ever called at runtime, so the
 * first parameter is the input unless a second one arrives.
 */
function withLowercasedUsername<TInput extends { username: string }, TOutput>(
  handler: (input: TInput) => Promise<TOutput>,
) {
  return (contextOrInput: unknown, maybeInput?: TInput): Promise<TOutput> => {
    const input = (maybeInput ?? contextOrInput) as TInput;
    return handler({ ...input, username: input.username.toLowerCase() });
  };
}

const services = {
  handleSignIn: withLowercasedUsername(signIn),
  handleForgotPassword: withLowercasedUsername(resetPassword),
  handleForgotPasswordSubmit: withLowercasedUsername(confirmResetPassword),
};

/** Replaces the amplifySlot="header" template. */
function AuthHeader() {
  // Read once on mount. The Authenticator sits outside the router, so this
  // reads window.location directly rather than useSearchParams.
  const sessionExpired = useMemo(
    () => new URLSearchParams(window.location.search).get('sessionExpired') === 'true',
    [],
  );

  return (
    <header className="pt-8 pb-2">
      {sessionExpired && (
        <p
          role="status"
          className="mx-auto mb-4 max-w-sm text-center text-base font-semibold text-red-600"
        >
          Your session has expired due to inactivity. Please log in again.
        </p>
      )}
      <div className="flex justify-center px-6">
        <img
          src="/assets/intraday-header-logo-2line.png"
          alt="Intraday Vision"
          className="h-auto max-w-[260px]"
        />
      </div>
    </header>
  );
}

/**
 * Everything the Angular AppComponent did on the `signedIn` Hub event and on
 * authStatus changes. Mounted only once the user is authenticated.
 */
function AuthenticatedApp() {
  const queryClient = useQueryClient();
  const requestAdminAccess = useRequestAdminAccess();

  useEffect(() => {
    updateLastActivity();

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signedIn') {
        // Fresh session: drop anything cached for the previous user.
        queryClient.clear();
        updateLastActivity();

        if (await isAdministrator()) {
          // Sets the CloudFront signed cookie that unlocks /admin/* assets.
          requestAdminAccess.mutate();
        }
      }
      if (payload.event === 'signedOut') {
        queryClient.clear();
      }
    });

    // Also run on mount, for a reload where the session is already valid and
    // no signedIn event will fire.
    void (async () => {
      if (await isAdministrator()) {
        requestAdminAccess.mutate();
      }
    })();

    return unsubscribe;
    // requestAdminAccess is stable enough for this one-time wiring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  return <RouterProvider router={router} />;
}

function AuthGate() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  if (authStatus !== 'authenticated') return null;
  return <AuthenticatedApp />;
}

export default function App() {
  useEffect(() => {
    runCookieConsent();
  }, []);

  return (
    <Authenticator hideSignUp services={services} components={{ Header: AuthHeader }}>
      <AuthGate />
    </Authenticator>
  );
}
